
import OSM , {ATTRIBUTION} from "ol/source/OSM";
import TileLayer from "ol/layer/Tile";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import {Icon, Style} from "ol/style";
import {Vector as VectorLayer} from "ol/layer";
import VectorSource from "ol/source/Vector";
import {fromLonLat} from "ol/proj";

import InputSearchAddressArea from "../ui/InputSearchAddressArea.js";

import global from "../global.js";

const log = global.log.getLogger("f_mtac.view.OpenLayersDriverOSM");
log.setLevel(log.levels.WARN);

/**
 * The OpenLayersDriverOSM class is responsible for:
 *  - loading OSM map (basic and cyclo)
 *  - search OSM address (with Nominatim)
 *  - switch between 2 defined layers
 */
const OpenLayersDriverOSM = function(view_, olDriver_) {

    log.debug("OpenLayersDriverOSM", {olDriver_});

    //var view = view_;
    var olDriver = olDriver_;
    var olMap = null; // OpenLayers Map
    var olView = null; // OpenLayers Map View
    var olOSM = null; // layer handler for OSM
    var osm = null; // OSM basic layer
    var cyclosm = null; // OSM cycle map layer
    var markerLayer = null;

    const max_zoom_osm = 21; // max authorized zoom value
    const max_zoom_cyclosm = 21; // max authorized zoom value
    const tile_size = 256;
    let mapTypeId="";

    /**
     * All properties
     */
    Object.defineProperties(this, {
        mapBasicLayerType:{
            set: (value) => {
                log.debug("set mapTypeId", {value});
                mapTypeId = value;
                olDriver.mapBasicLayerType = value;
            },
            get: () => mapTypeId
        },
        osm:{
            set: () => {},
            get: () => osm
        },
        cyclosm:{
            set: () => {},
            get: () => cyclosm
        },
    });

    this.activate = function() {
        olMap = olDriver.ol_map;
        olView = olDriver.ol_map.getView();

        /**
         * When OpenLayers change:resolution event is fired
         */
        olView.on("change:resolution", function() {
            // pas sûr que ça soit utile...
            /*view.hide_svg();
            olDriver.launch_transform();
            view.show_svg();*/
        });
    };

    /**
     * Add OSM map as an OpenLayers layer
     * @param type map Type, example maps.MapTypeId.ROADMAP
     * @returns {boolean} false in case of exception (google map not loaded)
     */
    this.add_map_layer = function(type) {
        if (olDriver.ol_map != null) {
            log.debug("add_osm_map_layer", {type});
            try {
                olDriver.is_removing = false;
                if (type !== undefined) {
                    this.mapBasicLayerType = type;
                } else {
                    this.mapBasicLayerType = "OSM";
                }

                let href  = "https://www.openstreetmap.org/";
                cyclosm = new TileLayer({
                    source : new OSM({
                        url : "https://a.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png"}),
                    attribution: "Map data &copy; <a href="+href+">OpenStreetMap</a> contributors",
                    minZoom: 0,
                    maxZoom: max_zoom_cyclosm,
                });

                osm = new TileLayer({
                    source : new OSM({
                        attributions: [
                            "Map data &copy; <a href=" + href + ">OpenStreetMap</a> contributors",
                            ATTRIBUTION
                        ],
                        url : "https://tile.openstreetmap.org/{z}/{x}/{y}.png"}),
                    attribution: "Map data &copy; <a href="+href+">OpenStreetMap</a> contributors",
                    minZoom: 0,
                    maxZoom: max_zoom_osm,
                });

                olDriver.ol_map.addLayer(cyclosm);
                olDriver.ol_map.addLayer(osm);
                switch(type){
                    case "OSM":
                        cyclosm.setVisible(false);
                    break;
                    case "CYCLOSM":
                        osm.setVisible(false);
                    break;
                }
                // save the basic layer
                olOSM = osm;
                olDriver.ol_basic_layer = olOSM;
            } catch(error) {
                log.error("OSM map is not loaded", error);
                return false;
            }
            log.debug("add_osm_map_layer, out");
            return true;
        }
        log.error("olMap is not activated");
        return false;
    };

    /**
     * Remove the osm map layer (when the space is closed or sitac is changed)
     */
    this.remove_map_layer = function() {
        log.debug("remove_map_layer");
        if (olOSM !== null) {
            log.debug("remove_osm_map_layer");
            olDriver.is_removing = true;
            olDriver.ol_map.removeLayer(osm);
            olDriver.ol_map.removeLayer(cyclosm);
            olOSM = null;
        }
        if (markerLayer !== null){
            olDriver.ol_map.removeLayer(markerLayer);
            markerLayer = null;
        }
    };

    /**
     * display marker on the last requested address
     */
    this.display_geocoding_address = function(address){
        log.debug("display_geocoding_address", address);
        let center = fromLonLat(address.location);

        var marker = new Feature({
            geometry: new Point(center),
        });

        var icon = new Icon(({
            anchor: [0.5, 1],
            anchorXUnits: "fraction",
            anchorYUnits: "fraction",
            src: "/mtac/static/marker-icon.png"
        }));

        marker.setStyle(new Style({
            image: icon,
        }));

        var vectorSource = new VectorSource({
            features: [marker]
        });

        markerLayer = new VectorLayer({
            source: vectorSource
        });
        
        olDriver.ol_map.addLayer(markerLayer);
    };

    /**
     * call the search address function
    **/
    this.search_address = async function(){
        let address = await InputSearchAddressArea.interact();
        log.debug("geocoding", {address});
        return address;
    };

    /**
     * Remove geocoding address marker
     */
    this.remove_geocoding_address = function(){
        log.debug("remove_geocoding_address");
        // remove marker if any
        if (markerLayer !== null) {
            olDriver.ol_map.removeLayer(markerLayer);
            markerLayer = null;
        }
    };

    // functions to get lon/lat to OSM tile numbers and inverse
    const lo2t = function(lon,zoom){
        return (lon+180)/360*Math.pow(2,zoom);
    };
    const la2t = function (lat,zoom){
        return (1-Math.log(Math.tan(lat*Math.PI/180) + 1/Math.cos(lat*Math.PI/180))/Math.PI)/2 *Math.pow(2,zoom);
    };
    const lon2tile = function(lon,zoom) {
        return Math.floor(lo2t(lon,zoom));
    };
    const lat2tile = function(lat,zoom) {
        return Math.floor(la2t(lat,zoom));
    };
    const tile2lon = function(x,zoom){
        return (x / Math.pow(2,zoom) * 360 - 180);
    };
    const tile2lat = function (y,z){
        var n=Math.PI-2*Math.PI*y/Math.pow(2,z);
        return (180/Math.PI*Math.atan(0.5*(Math.exp(n)-Math.exp(-n))));
    };

    /**
     * create static images tiles
     * @returns array of blob
     */
     this.static_map_create_blob_tiles = async function(gis_state) {
        log.debug("static_map_create_blob_tiles ",gis_state);
        var base_url = "";
        switch (gis_state.mapType) {
            case "OSM":
                base_url = "https://tile.openstreetmap.org/";
                break;
            case "CYCLOSM":
                base_url = "https://a.tile-cyclosm.openstreetmap.fr/cyclosm/";
                break;
            default :
                base_url = "https://tile.openstreetmap.org/";
                break;
        }
        let zoom = gis_state.zoom;
        let tile_data_array = [];
        let blob_array = [];
        let tile_numbers = gis_state.tiles.length;
        
        for (let i=0; i<tile_numbers; i++) {
            var data = {};
            var coordinates = gis_state.tiles[i].coordinates;
            tile_data_array.push({data, coordinates});
        }

        // as the openstreetmap rounds the tile coordinates in order to have entire numbers tile coordinates (example : https://tile.openstreetmap.org/18/132760/90157.png)
        // we have to find the pixel difference between :
        // the asked center of the tile (stored in gis_state.tiles)
        // and the pixel coordinates of the center tile calculated by openstreetmap
        // calcul is done on the first tile (it is the same pixel diff for all tiles)

        // pixels of the wanted center tile:
        var pixel0 = global.view.ol_driver.from_lon_lat_position_to_pixel([gis_state.tiles[0].center[0], gis_state.tiles[0].center[1]]);
        // pixels of the first tile rounded:
        let xosm0 = lon2tile(gis_state.tiles[0].center[0], zoom);
        let yosm0 = lat2tile(gis_state.tiles[0].center[1], zoom);
        var posx = tile2lon(xosm0, zoom);
        var posy = tile2lat(yosm0, zoom);
        var pixel0Rounded = global.view.ol_driver.from_lon_lat_position_to_pixel([posx, posy]);
        var diffx = pixel0[0] - pixel0Rounded[0] - 128; // - 128 because the tile to lon/lat function returns the coordinates of the top left corner of tile , not he center
        var diffy = pixel0[1] - pixel0Rounded[1] - 128;

        // call static map api to create cache file
        for (var index in tile_data_array) {
            let url = base_url;
            let xosm = lon2tile(gis_state.tiles[index].center[0], zoom);
            let yosm = lat2tile(gis_state.tiles[index].center[1], zoom);
            url = url + zoom + "/" + xosm + "/" + yosm + ".png";
            var blob = await this.fetch_static_image(url, tile_data_array[index].data);
            let x = gis_state.tiles[index].coordinates.x - diffx;
            let y = gis_state.tiles[index].coordinates.y - diffy;
            blob_array.push({blob: blob, coordinates: {x, y}});
        }
        log.debug("blob_array ",blob_array);
        return blob_array;    
    };

    this.fetch_static_image = async (url, data) => {
        return global.server.mtac_static_media_retrieve({url: url, data: data});
    };
    

    /**
     * Switch OSM map type from OSM to CYCLOSM.
     * Change the maximum zoom authorized
    **/
    this.switch_map_basic_layer = function (fromAssistant) {
        if (olOSM != null){
            if (osm.getVisible() === true){
                osm.setVisible(false);
                cyclosm.setVisible(true);
                this.mapBasicLayerType = "CYCLOSM";
            } else {
                osm.setVisible(true);
                cyclosm.setVisible(false);
                this.mapBasicLayerType = "OSM";
            }
            
            // si on n'est pas dans l'Assistant => créer le gis cache
            if (!fromAssistant) {
                let fallback_media_created = true;
                let object = global.scene.map.get_map_object();
                log.debug("mapTypeId ", mapTypeId);
                if ((mapTypeId === "OSM" && object.attributes.dataMediaLayer1 == undefined) ||
                    (mapTypeId === "CYCLOSM" && object.attributes.dataMediaLayer2 == undefined)) {
                    fallback_media_created = false;
                }
                if (!fallback_media_created){
                    global.scene.map.map_gis.create_gis_fallback_image(mapTypeId);
                } else {
                    log.debug("this.mapBasicLayerType ", this.mapBasicLayerType);
                    switch (this.mapBasicLayerType) {
                        case "OSM":
                            global.scene.map.set_map_object({
                                media_id: object.attributes.dataMediaLayer1
                            });
                            global.view.save_map_gis_context(object.attributes.dataMediaLayer1);
                        break;
                        case "CYCLOSM":
                            global.scene.map.set_map_object({
                                media_id: object.attributes.dataMediaLayer2
                            });
                            global.view.save_map_gis_context(object.attributes.dataMediaLayer2);
                        break;
                    }
                }
            }

            // change max zoom
            let max_zoom = this.get_max_zoom();
            olMap.getView().setMaxZoom(max_zoom);

            // change current zoom if necessary
            let current_zoom = olMap.getView().getZoom();
            if (current_zoom > max_zoom) {
                olMap.getView().setZoom(max_zoom);
            }
        }
    };

    this.update_map_basiclayer_type = function (type) {
        this.mapBasicLayerType = type;
        switch(type){
            case "OSM":
                osm.setVisible(true);
                cyclosm.setVisible(false);
            break;
            case "CYCLOSM":
                osm.setVisible(false);
                cyclosm.setVisible(true);
            break;
        }
    };

    /**
     * Get the max zoom values for a given map type
     * @returns {number} the max zoom value
     */
    this.get_max_zoom = function () {
        log.debug("get_max_zoom mapType = %s", mapTypeId);
        if (mapTypeId == "CYCLOSM") {
            return max_zoom_cyclosm;
        }
        return max_zoom_osm;
    };

    /**
     * Get the tile size used in static map feature
     */
    this.get_tile_size = function () {
        return tile_size;
    };

    /**
     * set the fallback media id (used when offline or gis not authorized)
     */
     this.set_map_fallback_media = function(media_id, map_type, object) {
        log.debug("set_map_fallback_media", {media_id, map_type, object});
        if (map_type == "CYCLOSM" && object.attributes.dataMediaLayer2 == undefined) {
            let media_layer2_id = {media_layer2_id: media_id};
            return global.scene.map.set_map_object(media_layer2_id);
        } else {
            let media_layer1_id = {media_layer1_id: media_id};
            return global.scene.map.set_map_object(media_layer1_id);
        }
    };


};


export default OpenLayersDriverOSM;